import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import { supabase } from '../../lib/supabase';

interface NFTCollection {
  id: number;
  name: string;
  description: string;
  floor_price: number;
  volume_24h: number;
  total_items: number;
  total_owners: number;
  image_url: string;
}

interface NFT {
  id: number;
  collection_id: number;
  token_id: string;
  name: string;
  description: string;
  image_url: string;
  price: number;
  metadata: any;
  collection: {
    name: string;
    floor_price: number;
  };
}

export default function PepVerse() {
  const navigate = useNavigate();

  // State management
  const [activeSection, setActiveSection] = useState('marketplace');
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);
  const [showNFTPurchase, setShowNFTPurchase] = useState(false);
  const [showNFTDetails, setShowNFTDetails] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState<NFT | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<NFTCollection | null>(null);
  const [userBalance, setUserBalance] = useState(0);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState<NFTCollection[]>([]);
  const [nfts, setNFTs] = useState<NFT[]>([]);
  const [userNFTs, setUserNFTs] = useState<NFT[]>([]);

  const [avatarConfig, setAvatarConfig] = useState({
    head: 0,
    body: 0,
    accessory: 0,
    animation: 0,
    color: '#06b6d4'
  });

  const [avatarPreview, setAvatarPreview] = useState({
    headStyle: 'Robot',
    bodyStyle: 'Cyber Suit',
    accessoryStyle: 'Neon Visor',
    animationStyle: 'Idle'
  });

  // Load initial data
  useEffect(() => {
    loadUserBalance();
    loadCollections();
    loadUserNFTs();
  }, []);

  const loadUserBalance = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Create wallet if it doesn't exist
        const { data: wallet, error: walletError } = await supabase
          .from('wallets')
          .select('pep_balance')
          .eq('user_id', user.id)
          .single();
        
        if (walletError && walletError.code === 'PGRST116') {
          // Wallet doesn't exist, create it
          await supabase.from('wallets').insert({
            user_id: user.id,
            pep_balance: 100.0 // Give new users 100 PEP to start
          });
          setUserBalance(100.0);
        } else if (wallet) {
          setUserBalance(wallet.pep_balance || 0);
        }
      }
    } catch (error) {
      console.error('Error loading balance:', error);
      // Set default balance on error
      setUserBalance(0);
    }
  };

  const loadCollections = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No session, skipping collections load');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/nft-marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'get_collections' })
      });

      if (!response.ok) {
        console.log('Collections API returned error status:', response.status);
        return;
      }

      const result = await response.json();
      if (result.collections && Array.isArray(result.collections)) {
        setCollections(result.collections);
      } else if (result.error) {
        console.log('Collections API error:', result.error);
      }
    } catch (error) {
      console.log('Error loading collections:', error);
      // Silently fail - collections are optional
    }
  };

  const loadNFTsForCollection = async (collectionId: number) => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No session, skipping NFTs load');
        setLoading(false);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/nft-marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'get_nfts', collectionId })
      });

      if (!response.ok) {
        console.log('NFTs API returned error status:', response.status);
        setLoading(false);
        return;
      }

      const result = await response.json();
      if (result.nfts && Array.isArray(result.nfts)) {
        setNFTs(result.nfts);
      } else if (result.error) {
        console.log('NFTs API error:', result.error);
      }
    } catch (error) {
      console.log('Error loading NFTs:', error);
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const loadUserNFTs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user, skipping user NFTs load');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No session, skipping user NFTs load');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/nft-marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'get_user_nfts' })
      });

      if (!response.ok) {
        console.log('User NFTs API returned error status:', response.status);
        return;
      }

      const result = await response.json();
      if (result.nfts && Array.isArray(result.nfts)) {
        setUserNFTs(result.nfts);
      } else if (result.error) {
        console.log('User NFTs API error:', result.error);
      }
    } catch (error) {
      console.log('Error loading user NFTs:', error);
      // Silently fail - user NFTs are optional
    }
  };

  const web3Features = [
    {
      name: 'Decentralized NFT Marketplace',
      description: 'Trade PepCoin NFTs on-chain with smart contract security',
      users: '12,450',
      type: 'DeFi',
      image: 'https://public.readdy.ai/ai/img_res/e830541fe541a2e64ad3cb2053785232.jpg',
    },
    {
      name: 'DAO Governance Hub',
      description: 'Vote on proposals and shape the future of PepVerse ecosystem',
      users: '8,920',
      type: 'DAO',
      image: 'https://readdy.ai/api/search-image?query=modern%20virtual%20governance%20chamber%20with%20voting%20displays%20and%20blockchain%20consensus%20visualization%20decentralized%20autonomous%20organization%20web3%20space&width=600&height=400&seq=daohub1&orientation=landscape',
    },
    {
      name: 'DeFi Staking Arena',
      description: 'Stake your PepCoins and NFTs to earn passive rewards',
      users: '15,680',
      type: 'DeFi',
      image: 'https://readdy.ai/api/search-image?query=luxury%20virtual%20DeFi%20staking%20platform%20with%20cryptocurrency%20displays%20and%20yield%20farming%20visualization%20elegant%20blockchain%20design%20with%20gold%20and%20cyan%20accents&width=600&height=400&seq=defistake1&orientation=landscape',
    },
  ];

  const web3Customization = [
    { 
      category: 'Blockchain Avatars', 
      items: 150, 
      price: '0.1-2.5 PEP',
      options: ['Ethereum Punk', 'Solana Ape', 'Polygon NFT', 'Cardano Hero', 'BSC Dragon']
    },
    { 
      category: 'Smart Contract Skins', 
      items: 200, 
      price: '0.2-3.0 PEP',
      options: ['DeFi Armor', 'DAO Suit', 'Web3 Casual', 'Crypto Formal', 'NFT Warrior']
    },
    { 
      category: 'Crypto Accessories', 
      items: 300, 
      price: '0.05-1.5 PEP',
      options: ['Blockchain Visor', 'Token Crown', 'NFT Wings', 'DeFi Halo', 'Crypto Sword']
    },
    { 
      category: 'Web3 Animations', 
      items: 75, 
      price: '0.3-5.0 PEP',
      options: ['Mining', 'Trading', 'Staking', 'Minting', 'Governance']
    },
  ];

  const web3Artifacts = [
    {
      name: 'Genesis Block Token',
      description: 'The first-ever PepCoin NFT minted on the blockchain. This legendary artifact represents the birth of decentralized PepCoin finance and grants exclusive DAO voting power.',
      rarity: 'Legendary',
      price: '500.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=legendary%20golden%20blockchain%20genesis%20block%20with%20glowing%20cryptocurrency%20symbols%20floating%20in%20cosmic%20space%20with%20digital%20aura%20and%20holographic%20blockchain%20network%20effects&width=400&height=400&seq=genesisblock1&orientation=squarish',
      traits: { Blockchain: 'Ethereum', Power: 'Maximum', Rarity: 'Unique', Utility: 'DAO Voting' }
    },
    {
      name: 'Diamond Hands NFT',
      description: 'Forged from the strongest HODLers in the crypto community. This NFT grants 2x staking rewards and exclusive access to premium DeFi pools.',
      rarity: 'Epic',
      price: '250.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=epic%20diamond%20crystalline%20hands%20holding%20glowing%20cryptocurrency%20coins%20with%20sparkling%20gemstone%20effects%20and%20blockchain%20energy%20rainbow%20reflections&width=400&height=400&seq=diamondhands1&orientation=squarish',
      traits: { Material: 'Diamond', Staking: '2x Rewards', Holder: 'True Believer', Utility: 'DeFi Access' }
    },
    {
      name: 'DeFi Yield Booster',
      description: 'Infused with pure yield farming energy. This artifact increases your staking APY by 50% and provides early access to new liquidity pools.',
      rarity: 'Epic',
      price: '200.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20DeFi%20yield%20farming%20device%20with%20glowing%20green%20energy%20streams%20and%20cryptocurrency%20symbols%20floating%20around%20golden%20core%20with%20blockchain%20effects&width=400&height=400&seq=yieldboost1&orientation=squarish',
      traits: { Type: 'DeFi Tool', APY: '+50%', Access: 'Early Pools', Utility: 'Yield Farming' }
    },
    {
      name: 'DAO Governance Token',
      description: 'The ultimate governance artifact that channels the collective power of decentralized decision-making. Holders gain 10x voting power in all DAO proposals.',
      rarity: 'Rare',
      price: '150.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=charismatic%20governance%20token%20with%20golden%20crown%20and%20voting%20symbols%20surrounded%20by%20democratic%20energy%20aura%20with%20blockchain%20network%20effects%20modern%20art%20style&width=400&height=400&seq=daotoken1&orientation=squarish',
      traits: { Governance: '10x Votes', Influence: 'DAO Leader', Status: 'Council', Utility: 'Proposals' }
    },
    {
      name: 'Smart Contract Optimizer',
      description: 'Blessed by the blockchain gods with eternal gas efficiency. This artifact reduces all transaction fees by 75% and speeds up contract execution.',
      rarity: 'Rare',
      price: '120.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=advanced%20smart%20contract%20optimization%20chip%20with%20bright%20blue%20circuit%20patterns%20and%20blockchain%20code%20flowing%20through%20golden%20pathways%20with%20efficiency%20aura&width=400&height=400&seq=contractopt1&orientation=squarish',
      traits: { Gas: '-75% Fees', Speed: 'Instant', Efficiency: 'Maximum', Utility: 'Transactions' }
    },
    {
      name: 'Web3 Community Badge',
      description: 'Forged from the bonds of the strongest crypto community. This badge represents unity, shared vision, and grants access to exclusive Web3 events.',
      rarity: 'Common',
      price: '75.0 PEP',
      image: 'https://readdy.ai/api/search-image?query=friendly%20community%20badge%20with%20connected%20blockchain%20network%20nodes%20and%20unity%20symbols%20warm%20golden%20glow%20representing%20decentralized%20community%20spirit%20wholesome%20style&width=400&height=400&seq=web3badge1&orientation=squarish',
      traits: { Unity: 'Strong', Community: 'Global', Access: 'Events', Utility: 'Networking' }
    }
  ];

  const handleAvatarChange = (category: string, index: number) => {
    const categoryMap: any = {
      'Blockchain Avatars': 'head',
      'Smart Contract Skins': 'body',
      'Crypto Accessories': 'accessory',
      'Web3 Animations': 'animation'
    };
    
    const key = categoryMap[category];
    setAvatarConfig(prev => ({ ...prev, [key]: index }));
    
    // Update preview
    const categoryData = web3Customization.find(c => c.category === category);
    if (categoryData) {
      const previewMap: any = {
        'Blockchain Avatars': 'headStyle',
        'Smart Contract Skins': 'bodyStyle',
        'Crypto Accessories': 'accessoryStyle',
        'Web3 Animations': 'animationStyle'
      };
      setAvatarPreview(prev => ({
        ...prev,
        [previewMap[category]]: categoryData.options[index]
      }));
    }
  };

  const saveAvatar = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Please sign in to save your avatar');
        navigate('/auth/signin');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/nft-marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ 
          action: 'save_avatar',
          avatarConfig: {
            config: avatarConfig,
            preview: avatarPreview
          }
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('Web3 Avatar minted successfully! 2.5 PEP deducted from your balance.');
        setUserBalance(result.newBalance);
        setShowAvatarBuilder(false);
      } else {
        alert(result.error || 'Failed to mint avatar');
      }
    } catch (error) {
      console.error('Error minting avatar:', error);
      alert('Failed to mint avatar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const purchaseNFT = async () => {
    if (!selectedNFT) return;

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Please sign in to purchase NFTs');
        navigate('/auth/signin');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/nft-marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ 
          action: 'purchase_nft',
          nftId: selectedNFT.id
        })
      });

      const result = await response.json();
      if (result.success) {
        setUserBalance(result.newBalance);
        setPurchaseSuccess(true);
        loadUserNFTs(); // Refresh user's NFT collection
        
        setTimeout(() => {
          setPurchaseSuccess(false);
          setShowNFTPurchase(false);
          setSelectedNFT(null);
        }, 2000);
      } else {
        alert(result.error || 'Failed to purchase NFT');
      }
    } catch (error) {
      console.error('Error purchasing NFT:', error);
      alert('Failed to purchase NFT. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const enterWeb3Space = (spaceName: string) => {
    alert(`Connecting to ${spaceName}... Initializing Web3 environment...`);
    // In a real implementation, this would connect to blockchain
  };

  const viewCollectionNFTs = (collection: NFTCollection) => {
    setSelectedCollection(collection);
    loadNFTsForCollection(collection.id);
    setActiveSection('collection_view');
  };

  const viewNFTDetails = (nft: NFT) => {
    setSelectedNFT(nft);
    setShowNFTDetails(true);
  };

  // Add new handler for "Start Playing Now" button
  const handleStartPlaying = () => {
    // Navigate to game page
    navigate('/game');
  };

  // Add handler for Avatar Studio button
  const handleAvatarStudio = () => {
    setActiveSection('avatars');
    setShowAvatarBuilder(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-between sm:justify-start">
              <div className="flex items-center gap-2 sm:gap-3">
                <img 
                  src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                  alt="PepCoin Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                />
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    PepVerse
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Your Digital Universe</p>
                </div>
              </div>
              
              {/* Mobile Balance Display */}
              <div className="sm:hidden bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/20">
                <div className="text-xs text-gray-400">Balance</div>
                <div className="text-sm font-bold text-white">{userBalance.toLocaleString()} PEP</div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate('/wallet')}
                className="px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-wallet-3-fill mr-1 sm:mr-2"></i>
                Wallet
              </button>
              <button
                onClick={() => navigate('/trading')}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-stock-fill mr-1 sm:mr-2"></i>
                Trade
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-16">
        {/* Navigation Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 bg-gradient-to-r from-purple-500/10 via-cyan-500/10 to-purple-500/10 border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line mr-1 sm:mr-2"></i>
                  Back
                </button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate">PepVerse Web3 Metaverse</h1>
                  <p className="text-gray-400 text-xs sm:text-sm truncate">Decentralized blockchain ecosystem</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/wallet')}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-wallet-3-fill mr-1 sm:mr-2"></i>
                  Wallet
                </button>
                <button
                  onClick={() => navigate('/trading')}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-stock-fill mr-1 sm:mr-2"></i>
                  Trade
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div 
          className="relative px-4 sm:px-6 py-12 sm:py-16 md:py-24 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://readdy.ai/api/search-image?query=immersive%203D%20blockchain%20metaverse%20environment%20with%20floating%20cryptocurrency%20symbols%20and%20decentralized%20network%20nodes%20cyberpunk%20cityscape%20with%20neon%20lights%20and%20holographic%20Web3%20displays&width=1920&height=800&seq=web3verse1&orientation=landscape')`
          }}
        >
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4 sm:mb-6 backdrop-blur-sm">
              <i className="ri-links-fill text-cyan-400 text-sm sm:text-base"></i>
              <span className="text-cyan-400 text-xs sm:text-sm font-semibold">Web3 Metaverse</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black mb-4 sm:mb-6 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent px-4">
              Welcome to PepVerse Web3
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-4 max-w-2xl mx-auto px-4">
              Experience the ultimate decentralized metaverse powered by blockchain technology. Trade NFTs, participate in DAO governance, stake tokens, and connect with the global Web3 community.
            </p>
            <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-white/5 border border-white/10 rounded-xl inline-block mx-4">
              <div className="text-gray-400 text-xs sm:text-sm mb-1">Your PEP Balance</div>
              <div className="text-xl sm:text-2xl font-bold text-cyan-400">{userBalance.toFixed(2)} PEP</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <button 
                onClick={() => setActiveSection('spaces')}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg font-semibold text-white text-sm sm:text-base hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-rocket-2-fill mr-2"></i>
                Enter Web3 Space
              </button>
              <button 
                onClick={handleAvatarStudio}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-white/5 border border-white/20 rounded-lg font-semibold text-white text-sm sm:text-base hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-settings-fill mr-2"></i>
                Mint Avatar NFT
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="px-4 sm:px-6 py-6 sm:py-8 bg-gradient-to-r from-black via-gray-900 to-black border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1 bg-white/5 p-1 rounded-xl w-full overflow-x-auto scrollbar-hide">
              {[
                { id: 'marketplace', label: 'NFT Marketplace', icon: 'ri-store-3-fill' },
                { id: 'collection', label: 'My NFTs', icon: 'ri-gallery-fill' },
                { id: 'spaces', label: 'Web3 Spaces', icon: 'ri-building-4-fill' },
                { id: 'avatars', label: 'Avatar Minting', icon: 'ri-user-star-fill' },
                { id: 'events', label: 'DAO Events', icon: 'ri-live-fill' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 flex items-center gap-1 sm:gap-2 whitespace-nowrap cursor-pointer ${
                    activeSection === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <i className={`${tab.icon} text-sm sm:text-base`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* NFT Marketplace */}
          {activeSection === 'marketplace' && (
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">NFT Marketplace</h2>
                <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto px-4">
                  Discover, buy, and sell unique digital assets in the PepVerse ecosystem
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {collections.map((collection) => (
                  <div
                    key={collection.id}
                    className="group p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-cyan-500/50 transition-all duration-300"
                  >
                    <div className="w-full aspect-square rounded-xl overflow-hidden mb-3 sm:mb-4">
                      <img 
                        src={collection.image_url}
                        alt={collection.name} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    
                    <h3 className="text-base sm:text-lg font-bold text-white mb-2">{collection.name}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{collection.description}</p>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm mb-3 sm:mb-4">
                      <div>
                        <div className="text-gray-400">Floor Price</div>
                        <div className="text-cyan-400 font-semibold">{collection.floor_price} PEP</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Volume</div>
                        <div className="text-purple-400 font-semibold">{collection.volume_24h.toLocaleString()} PEP</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Items</div>
                        <div className="text-white font-semibold">{collection.total_items.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Owners</div>
                        <div className="text-green-400 font-semibold">{collection.total_owners.toLocaleString()}</div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => viewCollectionNFTs(collection)}
                      className="w-full mt-3 sm:mt-4 p-2 sm:p-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-search-eye-fill mr-2"></i>
                      View Collection
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collection View */}
          {activeSection === 'collection_view' && selectedCollection && (
            <div className="space-y-6 sm:space-y-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <button
                  onClick={() => setActiveSection('marketplace')}
                  className="px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-fill mr-2"></i>
                  Back to Marketplace
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate">{selectedCollection.name}</h2>
                  <p className="text-gray-400 line-clamp-2">{selectedCollection.description}</p>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                  <p className="text-gray-400 mt-4 text-sm">Loading NFTs...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {nfts.map((nft) => (
                    <div
                      key={nft.id}
                      className="group p-3 sm:p-4 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all duration-300"
                    >
                      <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3">
                        <img 
                          src={nft.image_url}
                          alt={nft.name} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      
                      <h4 className="text-white text-sm sm:text-base font-semibold mb-1 truncate">{nft.name}</h4>
                      <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">{nft.description}</p>
                      
                      <div className="flex items-center justify-between mb-2 sm:mb-3">
                        <span className="text-cyan-400 text-sm sm:text-base font-bold">{nft.price} PEP</span>
                        {nft.metadata?.rarity && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            nft.metadata.rarity === 'Legendary' ? 'bg-yellow-500/20 text-yellow-400' :
                            nft.metadata.rarity === 'Epic' ? 'bg-purple-500/20 text-purple-400' :
                            nft.metadata.rarity === 'Rare' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {nft.metadata.rarity}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={() => viewNFTDetails(nft)}
                          className="flex-1 py-2 px-2 sm:px-3 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-search-2-fill mr-1"></i>
                          View
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedNFT(nft);
                            setShowNFTPurchase(true);
                          }}
                          className="flex-1 py-2 px-2 sm:px-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-shopping-cart-2-fill mr-1"></i>
                          Buy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Collection */}
          {activeSection === 'collection' && (
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">My NFT Collection</h2>
                <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto px-4">
                  View and manage your owned NFTs in the PepVerse ecosystem
                </p>
              </div>

              {userNFTs.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <i className="ri-image-2-fill text-5xl sm:text-6xl text-gray-600 mb-4"></i>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">No NFTs Yet</h3>
                  <p className="text-gray-400 text-sm mb-6">Start building your collection by purchasing NFTs from the marketplace</p>
                  <button 
                    onClick={() => setActiveSection('marketplace')}
                    className="px-4 sm:px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-sm sm:text-base font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-store-2-fill mr-2"></i>
                    Browse Marketplace
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {userNFTs.map((nft) => (
                    <div
                      key={nft.id}
                      className="group p-3 sm:p-4 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all duration-300"
                    >
                      <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3">
                        <img 
                          src={nft.image_url}
                          alt={nft.name} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      
                      <h4 className="text-white text-sm sm:text-base font-semibold mb-1 truncate">{nft.name}</h4>
                      <p className="text-gray-400 text-xs sm:text-sm mb-2">{nft.collection.name}</p>
                      
                      <div className="flex items-center justify-between mb-2 sm:mb-3">
                        <span className="text-cyan-400 text-sm sm:text-base font-bold">{nft.price} PEP</span>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                          Owned
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => viewNFTDetails(nft)}
                        className="w-full py-2 px-2 sm:px-3 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-info-fill mr-2"></i>
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Web3 Spaces */}
          {activeSection === 'spaces' && (
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Web3 Decentralized Spaces</h2>
                <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto px-4">
                  Explore blockchain-powered environments for DeFi, DAO governance, and NFT trading
                </p>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {web3Features.map((space, idx) => (
                  <div
                    key={idx}
                    className="group p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-cyan-500/50 transition-all duration-300"
                  >
                    <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 items-center">
                      <div className="lg:col-span-1">
                        <div className="w-full aspect-video rounded-xl overflow-hidden">
                          <img 
                            src={space.image}
                            alt={space.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      </div>
                      
                      <div className="lg:col-span-2">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2">
                          <h3 className="text-lg sm:text-xl font-bold text-white">{space.name}</h3>
                          <div className="flex items-center gap-2">
                            <span className="px-2 sm:px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs font-semibold">
                              {space.type}
                            </span>
                            <div className="flex items-center gap-1 sm:gap-2 text-gray-400 text-xs sm:text-sm">
                              <i className="ri-user-line"></i>
                              <span>{space.users} online</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-400 mb-3 sm:mb-4 text-xs sm:text-sm">{space.description}</p>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                          <button 
                            onClick={() => enterWeb3Space(space.name)}
                            className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-links-fill mr-2"></i>
                            Connect Wallet
                          </button>
                          <button className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
                            <i className="ri-information-line mr-2"></i>
                            Learn More
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avatar Studio - Web3 Version */}
          {activeSection === 'avatars' && (
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Web3 Avatar Minting Studio</h2>
                <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto px-4">
                  Create and mint your unique blockchain avatar NFT. Express your Web3 identity with exclusive crypto-themed accessories and legendary blockchain artifacts.
                </p>
              </div>

              {/* Web3 Artifacts Section */}
              <div className="mb-8 sm:mb-12">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full">
                    <i className="ri-links-fill text-white text-lg sm:text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-2xl font-bold text-white truncate">Blockchain Artifacts</h3>
                    <p className="text-gray-400 text-xs sm:text-sm truncate">Legendary Web3 items with real utility and governance power</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  {web3Artifacts.map((artifact, idx) => (
                    <div
                      key={idx}
                      className="group p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-cyan-500/50 transition-all duration-300"
                    >
                      <div className="w-full aspect-square rounded-xl overflow-hidden mb-3 sm:mb-4">
                        <img 
                          src={artifact.image}
                          alt={artifact.name} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-base sm:text-lg font-bold text-white truncate flex-1 min-w-0 mr-2">{artifact.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                          artifact.rarity === 'Legendary' ? 'bg-yellow-500/20 text-yellow-400' :
                          artifact.rarity === 'Epic' ? 'bg-purple-500/20 text-purple-400' :
                          artifact.rarity === 'Rare' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {artifact.rarity}
                        </span>
                      </div>
                      
                      <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3">{artifact.description}</p>
                      
                      <div className="space-y-2 mb-3 sm:mb-4">
                        {Object.entries(artifact.traits).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center">
                            <span className="text-gray-400">{key}</span>
                            <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm">
                              {value as string}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="text-cyan-400 font-bold text-base sm:text-lg">{artifact.price}</span>
                        <div className="flex items-center gap-1">
                          <i className="ri-links-fill text-cyan-400 text-sm"></i>
                          <span className="text-gray-400 text-xs sm:text-sm">On-Chain</span>
                        </div>
                      </div>
                      
                      <button 
                        onClick={() => {
                          if (userBalance >= parseFloat(artifact.price)) {
                            alert(`Minting ${artifact.name} NFT! Transaction will be recorded on blockchain.`);
                          } else {
                            alert(`Insufficient PEP balance. You need ${artifact.price} to mint this NFT.`);
                          }
                        }}
                        className="w-full py-2 px-3 sm:px-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-bold hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-nft-fill mr-2"></i>
                        Mint NFT
                      </button>
                    </div>
                  ))}
                </div>

                <div className="p-4 sm:p-6 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 border border-cyan-500/30 rounded-2xl">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex-shrink-0">
                      <i className="ri-links-fill text-white text-xl sm:text-2xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg sm:text-xl font-bold text-white truncate">About Blockchain Artifacts</h4>
                      <p className="text-gray-300 text-xs sm:text-sm truncate">Decentralized NFTs with real utility in the Web3 ecosystem</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6 text-xs sm:text-sm">
                    <div>
                      <h5 className="text-white font-semibold mb-2">🔗 On-Chain Ownership</h5>
                      <p className="text-gray-300 mb-3 sm:mb-4">All artifacts are minted as NFTs on the blockchain, ensuring true ownership and provable scarcity through smart contracts.</p>
                      
                      <h5 className="text-white font-semibold mb-2">⚡ Real Utility</h5>
                      <p className="text-gray-300">Each NFT provides tangible benefits: governance voting power, staking multipliers, reduced fees, and exclusive DeFi access.</p>
                    </div>
                    <div>
                      <h5 className="text-white font-semibold mb-2">🎯 Tradeable Assets</h5>
                      <p className="text-gray-300 mb-3 sm:mb-4">Artifacts can be traded on decentralized marketplaces, staked for rewards, or used as collateral in DeFi protocols.</p>
                      
                      <h5 className="text-white font-semibold mb-2">🌟 DAO Integration</h5>
                      <p className="text-gray-300">Rare artifacts grant enhanced voting power in DAO governance, allowing you to shape the future of the PepVerse ecosystem.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
                <div className="p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Web3 Avatar Customization</h3>
                  <div className="space-y-3 sm:space-y-4">
                    {web3Customization.map((category, idx) => (
                      <div key={idx} className="p-3 sm:p-4 bg-black/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white text-sm sm:text-base font-semibold truncate flex-1 min-w-0 mr-2">{category.category}</h4>
                          <span className="text-cyan-400 text-xs sm:text-sm font-medium whitespace-nowrap">{category.price}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs sm:text-sm mb-2 sm:mb-3">
                          <span className="text-gray-400">{category.items} items available</span>
                        </div>
                        <div className="grid grid-cols-5 gap-1 sm:gap-2">
                          {category.options.map((option, optIdx) => (
                            <button
                              key={optIdx}
                              onClick={() => handleAvatarChange(category.category, optIdx)}
                              className={`p-1.5 sm:p-2 rounded-lg text-xs transition-all duration-300 cursor-pointer ${
                                avatarConfig[category.category.toLowerCase().split(' ')[0] as keyof typeof avatarConfig] === optIdx
                                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
                              }`}
                              title={option}
                            >
                              <div className="font-bold">{optIdx + 1}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Avatar Preview */}
                <div className="p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">NFT Avatar Preview</h3>
                  <div className="aspect-square bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center shadow-2xl">
                        <div className="w-28 h-28 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center overflow-hidden">
                          <img 
                            src="https://readdy.ai/api/search-image?query=cute%20cartoon%20green%20frog%20face%20pepe%20style%20cryptocurrency%20mascot%20logo%20simple%20clean%20design%20on%20solid%20green%20background%20friendly%20expression&width=200&height=200&seq=pepcoinlogo1&orientation=squarish" 
                            alt="PepCoin Logo"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="text-white font-semibold">Web3 Avatar NFT:</div>
                        <div className="text-gray-300">Type: {avatarPreview.headStyle}</div>
                        <div className="text-gray-300">Skin: {avatarPreview.bodyStyle}</div>
                        <div className="text-gray-300">Item: {avatarPreview.accessoryStyle}</div>
                        <div className="text-gray-300">Action: {avatarPreview.animationStyle}</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <button 
                      onClick={() => setShowAvatarBuilder(true)}
                      className="w-full p-2.5 sm:p-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-nft-fill mr-2"></i>
                      Open Minting Studio
                    </button>
                    <button 
                      onClick={() => {
                        const randomHead = Math.floor(Math.random() * 5);
                        const randomBody = Math.floor(Math.random() * 5);
                        const randomAccessory = Math.floor(Math.random() * 5);
                        const randomAnimation = Math.floor(Math.random() * 5);
                        
                        setAvatarConfig({
                          head: randomHead,
                          body: randomBody,
                          accessory: randomAccessory,
                          animation: randomAnimation,
                          color: '#06b6d4'
                        });
                        
                        setAvatarPreview({
                          headStyle: web3Customization[0].options[randomHead],
                          bodyStyle: web3Customization[1].options[randomBody],
                          accessoryStyle: web3Customization[2].options[randomAccessory],
                          animationStyle: web3Customization[3].options[randomAnimation]
                        });
                      }}
                      className="w-full p-2.5 sm:p-3 bg-white/5 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shuffle-fill mr-2"></i>
                      Random Generate
                    </button>
                  </div>
                </div>
              </div>

              {/* Start Playing Now Button */}
              <div className="text-center mt-8 sm:mt-12">
                <div className="p-6 sm:p-8 bg-gradient-to-r from-purple-500/10 via-cyan-500/10 to-purple-500/10 border border-purple-500/30 rounded-3xl">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Ready for Web3 Gaming?</h3>
                  <p className="text-gray-300 text-xs sm:text-sm mb-4 sm:mb-6 max-w-2xl mx-auto px-4">
                    Your blockchain avatar is ready! Enter the PepCoin Web3 gaming arena and earn crypto rewards with your NFT character.
                  </p>
                  <button 
                    onClick={handleStartPlaying}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-gamepad-fill text-xl sm:text-2xl mr-2 sm:mr-3"></i>
                    Start Web3 Gaming
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* DAO Events */}
          {activeSection === 'events' && (
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">DAO Governance Events</h2>
                <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto px-4">
                  Participate in decentralized governance, vote on proposals, and shape the future of PepVerse
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                <div className="p-4 sm:p-6 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-purple-500/10 border border-cyan-500/30 rounded-2xl">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 text-xs sm:text-sm font-semibold">VOTING LIVE</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Proposal #42: DeFi Pool Expansion</h3>
                  <p className="text-gray-300 mb-3 sm:mb-4 text-xs sm:text-sm">
                    Vote on adding new liquidity pools and increasing staking rewards by 25%
                  </p>
                  <div className="flex items-center justify-between mb-3 sm:mb-4 text-xs sm:text-sm">
                    <span className="text-gray-400">Votes Cast: 8,245</span>
                    <span className="text-cyan-400">Ends in 1d 8h</span>
                  </div>
                  <button className="w-full p-2.5 sm:p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Cast Your Vote
                  </button>
                </div>

                <div className="p-4 sm:p-6 bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">NFT Airdrop Event</h3>
                  <p className="text-gray-300 mb-3 sm:mb-4 text-xs sm:text-sm">
                    Exclusive NFT drop for DAO members and active governance participants
                  </p>
                  <div className="flex items-center justify-between mb-3 sm:mb-4 text-xs sm:text-sm">
                    <span className="text-gray-400">Starts: Tomorrow 2PM UTC</span>
                    <span className="text-purple-400">Limited: 1000 NFTs</span>
                  </div>
                  <button className="w-full p-2.5 sm:p-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-notification-3-fill mr-2"></i>
                    Set Reminder
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Avatar Builder Modal */}
      {showAvatarBuilder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-4xl w-full border border-purple-500/30 shadow-2xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                  alt="PepCoin Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                />
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Avatar Builder</h2>
                  <p className="text-xs sm:text-sm text-gray-400">Create Your Unique PepVerse Identity</p>
                </div>
              </div>
              <button
                onClick={() => setShowAvatarBuilder(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <i className="ri-close-line text-lg sm:text-xl"></i>
              </button>
            </div>

            <div className="p-6 sm:p-8">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-white mb-4">Customize Your Avatar</h4>
                  {web3Customization.map((category, idx) => (
                    <div key={idx} className="p-4 bg-black/30 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-white font-semibold">{category.category}</h5>
                        <span className="text-cyan-400 text-sm">{category.price}</span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {category.options.map((option, optIdx) => (
                          <button
                            key={optIdx}
                            onClick={() => handleAvatarChange(category.category, optIdx)}
                            className={`p-3 rounded-lg text-xs transition-all duration-300 cursor-pointer ${
                              avatarConfig[category.category.toLowerCase().split(' ')[0] as keyof typeof avatarConfig] === optIdx
                                ? 'bg-cyan-500/20 text-cyan-400 border-2 border-cyan-500'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10 border-2 border-transparent'
                            }`}
                            title={option}
                          >
                            <div className="font-bold">{optIdx + 1}</div>
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Selected: {category.options[avatarConfig[category.category.toLowerCase().split(' ')[0] as keyof typeof avatarConfig] as number]}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-white mb-4">Preview</h4>
                  <div className="aspect-square bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center shadow-2xl">
                        <div className="w-28 h-28 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center overflow-hidden">
                          <img 
                            src="https://readdy.ai/api/search-image?query=cute%20cartoon%20green%20frog%20face%20pepe%20style%20cryptocurrency%20mascot%20logo%20simple%20clean%20design%20on%20solid%20green%20background%20friendly%20expression&width=200&height=200&seq=pepcoinlogo1&orientation=squarish" 
                            alt="PepCoin Logo"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="p-3 bg-black/50 rounded-lg">
                          <div className="text-gray-400 text-xs mb-1">Head</div>
                          <div className="text-white font-semibold">{avatarPreview.headStyle}</div>
                        </div>
                        <div className="p-3 bg-black/50 rounded-lg">
                          <div className="text-gray-400 text-xs mb-1">Body</div>
                          <div className="text-white font-semibold">{avatarPreview.bodyStyle}</div>
                        </div>
                        <div className="p-3 bg-black/50 rounded-lg">
                          <div className="text-gray-400 text-xs mb-1">Accessory</div>
                          <div className="text-white font-semibold">{avatarPreview.accessoryStyle}</div>
                        </div>
                        <div className="p-3 bg-black/50 rounded-lg">
                          <div className="text-gray-400 text-xs mb-1">Animation</div>
                          <div className="text-white font-semibold">{avatarPreview.animationStyle}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300">Total Cost:</span>
                      <span className="text-cyan-400 font-bold text-xl">2.5 PEP</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Your Balance:</span>
                      <span className="text-white font-semibold">{userBalance.toFixed(2)} PEP</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setShowAvatarBuilder(false)}
                  className="flex-1 py-3 bg-white/10 border border-white/20 rounded-xl font-bold text-white hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-fill mr-2"></i>
                  Cancel
                </button>
                <button
                  onClick={saveAvatar}
                  disabled={userBalance < 2.5 || loading}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl font-bold text-white hover:scale-105 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? (
                    <i className="ri-loader-4-fill animate-spin mr-2"></i>
                  ) : (
                    <i className="ri-save-3-fill mr-2"></i>
                  )}
                  Save Avatar (2.5 PEP)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NFT Purchase Modal */}
      {showNFTPurchase && selectedNFT && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="max-w-lg w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-6">
            {purchaseSuccess ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Purchase Successful!</h3>
                <p className="text-gray-300">Your NFT has been added to your collection</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white">Purchase NFT</h3>
                  <button
                    onClick={() => {
                      setShowNFTPurchase(false);
                      setSelectedNFT(null);
                    }}
                    className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-close-fill text-lg"></i>
                  </button>
                </div>

                <div className="mb-6">
                  <div className="w-full aspect-square rounded-xl overflow-hidden mb-4">
                    <img 
                      src={selectedNFT.image_url}
                      alt={selectedNFT.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{selectedNFT.name}</h4>
                  <p className="text-gray-400 text-sm mb-4">{selectedNFT.description}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div className="p-3 bg-black/30 rounded-lg">
                      <div className="text-gray-400 mb-1">Price</div>
                      <div className="text-cyan-400 font-bold text-lg">{selectedNFT.price} PEP</div>
                    </div>
                    <div className="p-3 bg-black/30 rounded-lg">
                      <div className="text-gray-400 mb-1">Your Balance</div>
                      <div className="text-white font-bold text-lg">{userBalance.toFixed(2)} PEP</div>
                    </div>
                  </div>
                  {selectedNFT.metadata && (
                    <div className="p-3 bg-black/30 rounded-lg">
                      <div className="text-gray-400 mb-2">Traits</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(selectedNFT.metadata.traits || {}).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs">
                            {key}: {value as string}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowNFTPurchase(false);
                      setSelectedNFT(null);
                    }}
                    className="flex-1 py-3 bg-white/10 border border-white/20 rounded-xl font-bold text-white hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-fill mr-2"></i>
                    Cancel
                  </button>
                  <button
                    onClick={purchaseNFT}
                    disabled={userBalance < selectedNFT.price || loading}
                    className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl font-bold text-white hover:scale-105 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {loading ? (
                      <i className="ri-loader-4-fill animate-spin mr-2"></i>
                    ) : (
                      <i className="ri-money-dollar-circle-fill mr-2"></i>
                    )}
                    Buy Now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* NFT Details Modal */}
      {showNFTDetails && selectedNFT && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">NFT Details</h3>
              <button
                onClick={() => {
                  setShowNFTDetails(false);
                  setSelectedNFT(null);
                }}
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 cursor-pointer"
              >
                <i className="ri-close-fill text-xl"></i>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="w-full aspect-square rounded-xl overflow-hidden mb-4">
                  <img 
                    src={selectedNFT.image_url}
                    alt={selectedNFT.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">{selectedNFT.name}</h4>
                  <p className="text-gray-400 text-sm mb-4">{selectedNFT.description}</p>
                </div>

                <div className="p-4 bg-black/30 rounded-xl">
                  <div className="text-gray-400 mb-2">Collection</div>
                  <div className="text-white font-semibold">{selectedNFT.collection.name}</div>
                </div>

                <div className="p-4 bg-black/30 rounded-xl">
                  <div className="text-gray-400 mb-2">Price</div>
                  <div className="text-cyan-400 font-bold text-2xl">{selectedNFT.price} PEP</div>
                </div>

                <div className="p-4 bg-black/30 rounded-xl">
                  <div className="text-gray-400 mb-2">Token ID</div>
                  <div className="text-white font-mono text-sm">{selectedNFT.token_id}</div>
                </div>

                {selectedNFT.metadata && (
                  <div className="p-4 bg-black/30 rounded-xl">
                    <div className="text-gray-400 mb-3">Traits</div>
                    <div className="space-y-2">
                      {Object.entries(selectedNFT.metadata.traits || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-gray-400">{key}</span>
                          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm">
                            {value as string}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
